import {
  DailyProductForPDF,
  IssueTaskCategory,
  IssueTaskDoc,
  PackingInfoProductEntry,
  PackingInfoSnapshot,
  PaperInvoice,
  ProductDoc,
  ProductSupplierDoc,
  ProductsForSupplierEntry,
} from '@gooduncles/gu-app-schema';
import jsPDF, { CellConfig } from 'jspdf';
import { groupBy, uniq } from 'lodash-es';
import { commerceIssueCategoryTable, issueTaskCategoryTable } from 'src/constants';

import { formatDate } from 'src/utils/date-util';
import { getProductInventories, getProductsByIds } from 'src/utils/firebase/firebase-short-cut';

import { myFont } from './font-for-jspdf';

const sortRules = [
  [IssueTaskCategory.DELIVERY],
  [IssueTaskCategory.PICK_UP],
  [IssueTaskCategory.QUICK],
  [IssueTaskCategory.REFUND],
  [IssueTaskCategory.ADDITIONAL_CHARGE],
  [IssueTaskCategory.OTHER],
  [IssueTaskCategory.INTERNAL],
  '배송',
  '회수',
  '식당환불',
  '거래처반품',
  '거래처매입',
  '주문수정',
  '매입자료수정',
  '매출자료수정',
  '기타',
  '주문',
  '쿠폰할인',
  '보관',
  'issue',
  'product',
];

/**
 * 거래처별 인쇄물에서 사용하는 거래처 표시명. 그룹 정렬을 위해 groupId를 앞에 붙인다.
 */
const getSupplierLabel = (supplier: ProductSupplierDoc | undefined) =>
  `${supplier?.groupId ?? ''}${supplier?.name ?? '기타'}`;

/**
 * 상품 id로 상품 문서를 조회한다. 존재하지 않는 id는 키가 없다.
 * ponytail: 이슈 상품 수(하루 수십건) 만큼 개별 조회한다. 늘어나면 상품 목록 캐시에서 찾도록 바꿀 것.
 */
const getProductMapByIds = async (productIds: string[]) => {
  const ids = uniq(productIds.filter((id) => id.length > 0));
  if (ids.length === 0) return {};
  const products = await getProductsByIds(ids);
  return Object.fromEntries(
    products.filter((product): product is ProductDoc => product !== null).map((product) => [product.productId, product])
  );
};

/**
 * 거래명세서 데이터를 오늘의 주문 품목 PDF 데이터로 변환한다.
 * FIXME: issue migration ✅
 */
const convertPaperInvoicesToDailyProduct = (
  paperInvoices: PaperInvoice[],
  productSuppliers: ProductSupplierDoc[],
  exceptBag = false
) => {
  // 1. convert orderProduct
  const products: DailyProductForPDF[] = paperInvoices
    .flatMap((invoice) => {
      const orderProducts = !exceptBag
        ? invoice.order?.products ?? []
        : (invoice.order?.products ?? []).filter((p) => p.packing !== 'bag');
      return orderProducts.map((product) => {
        const supplierNameWithGroupId = getSupplierLabel(productSuppliers.find((ps) => ps._id === product.supplierId));
        return {
          partnerKey: invoice.sortKey,
          storeName: invoice.store.storeNickname,
          label: product.fullName,
          quantity: String(product.volume),
          supplierName: supplierNameWithGroupId,
          sortKey: 'product',
          id: product.productId,
        };
      });
    })
    // 담당자
    .sort((a, b) => a.partnerKey - b.partnerKey)
    // 수량(오름차순)
    .sort((a, b) => a.quantity.localeCompare(b.quantity))
    // 상품명
    .sort((a, b) => a.label.localeCompare(b.label, 'ko'));

  // 2. convert storeIssue
  const storeIssues: DailyProductForPDF[] = paperInvoices
    .flatMap((invoice) => {
      const issues = invoice.storeIssues ?? [];
      return issues.map((issue) => {
        const prefix = issue.category ? commerceIssueCategoryTable[issue.category] ?? '' : '';
        const label = issue.message ? prefix + issue.message : issue.memo;
        return {
          partnerKey: invoice.sortKey,
          storeName: invoice.store.storeNickname,
          label: label ?? '?',
          quantity: String(issue.volume ?? 0),
          sortKey: issue.category ?? 'issue',
          id: issue.productId ?? '',
          supplierName: '기타',
        };
      });
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'ko'));

  const issueTaskProducts: DailyProductForPDF[] = paperInvoices.flatMap((invoice) => {
    const issueTasks = invoice.issueTasks ?? [];
    return issueTasks.flatMap((issueTask: IssueTaskDoc) => {
      const products = issueTask.products;
      if (!products || products.length === 0)
        return [
          {
            partnerKey: invoice.sortKey,
            storeName: invoice.store.storeNickname,
            label: `${issueTaskCategoryTable[issueTask.category]} ${issueTask.privateNote ?? ''}`,
            quantity: '1',
            sortKey: issueTask.category,
            id: issueTask._id,
            supplierName: '기타',
          },
        ];

      return products.map((product) => {
        return {
          partnerKey: invoice.sortKey,
          storeName: invoice.store.storeNickname,
          label: `${issueTaskCategoryTable[issueTask.category]} ${product.productName}`,
          quantity: String(product.quantity),
          sortKey: issueTask.category,
          id: product.productId,
          supplierName: '기타',
        };
      });
    });
  });

  return {
    products,
    storeIssues,
    issueTaskProducts,
  };
};

/**
 * FIXME: issue migration ✅
 */
export const downloadDailyProductList = async (
  date: string,
  paperInvoices: PaperInvoice[],
  productSuppliers: ProductSupplierDoc[]
) => {
  const { products, storeIssues, issueTaskProducts } = convertPaperInvoicesToDailyProduct(
    paperInvoices,
    productSuppliers,
    false
  );

  // 3. merge and sort
  const merged = [...products, ...storeIssues, ...issueTaskProducts]
    .sort((a, b) => sortRules.indexOf(a.sortKey) - sortRules.indexOf(b.sortKey))
    .map((item) => {
      const { sortKey, id, ...item0 } = item;
      sortKey;
      id;
      return {
        ...item0,
        partnerKey: String(item.partnerKey),
      };
    });

  const filename = `${formatDate(date, 'yyMMdd')}_오늘의주문품목.pdf`;
  const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
  const headers: CellConfig[] = [
    {
      name: 'partnerKey',
      prompt: '담당자',
      width: 28,
      align: 'center',
      padding: 0,
    },
    {
      name: 'storeName',
      prompt: '매장명',
      width: 74,
      align: 'center',
      padding: 0,
    },
    {
      name: 'label',
      prompt: '품명',
      width: 150,
      align: 'center',
      padding: 0,
    },
    {
      name: 'quantity',
      prompt: '수량',
      width: 20,
      align: 'center',
      padding: 0,
    },
  ];

  doc.addFileToVFS('NotoSansKR-Medium-normal.ttf', myFont);
  doc.addFont('NotoSansKR-Medium-normal.ttf', 'NotoSansKR-Medium', 'bold');
  doc.addFont('NotoSansKR-Medium-normal.ttf', 'NotoSansKR-Medium', 'normal');
  doc.setFont('NotoSansKR-Medium', 'bold');
  doc.setFont('NotoSansKR-Medium', 'normal');
  doc.table(1, 1, merged, headers, {});
  doc.save(filename);
};

/**
 * 거래처 & 파트너별 상품 목록을 생성
 * : 배송 팀장이 특정 상품에 대한 담당자를 추적할 때 사용한다.
 * FIXME: issue migration ✅
 */
export const getProductListForPartner = async (
  paperInvoices: PaperInvoice[],
  productSuppliers: ProductSupplierDoc[]
) => {
  const { storeIssues, products, issueTaskProducts } = convertPaperInvoicesToDailyProduct(
    paperInvoices,
    productSuppliers,
    true
  );
  // 이슈배송 상품은 거래처에서 받아와야 하는 물량이므로 주문 상품과 동일하게 취급한다.
  // : 품명과 거래처를 상품 문서에서 다시 채워 같은 행으로 합쳐지게 하고, 소분 상품도 동일하게 제외한다.
  // 상품이 특정되지 않는 이슈(메모만 있는 태스크 등)는 기존대로 '기타'에 남는다.
  const productMap = await getProductMapByIds(issueTaskProducts.map((issue) => issue.id));
  const issueProducts = issueTaskProducts.flatMap((issue) => {
    const product = productMap[issue.id];
    if (!product) return [issue];
    if (product.packing === 'bag') return [];
    return [
      {
        ...issue,
        label: product.fullName,
        supplierName: getSupplierLabel(productSuppliers.find((ps) => ps._id === product.supplierId)),
      },
    ];
  });
  const merged = [...storeIssues, ...products, ...issueProducts];
  // 이슈분이 섞인 칸을 인쇄물에서 구분하기 위한 키 목록. 그룹핑 키와 동일한 순서로 만든다.
  const issueKeys = new Set(
    [...storeIssues, ...issueProducts].map((issue) => `${issue.supplierName}|${issue.label}|${issue.partnerKey}`)
  );

  const productForSupplierEntry: ProductsForSupplierEntry = Object.fromEntries(
    Object.entries(groupBy(merged, 'supplierName')).map(([supplierName, products]) => {
      const groupByProductName = groupBy(products, 'label');
      const groupedList = Object.fromEntries(
        Object.entries(groupByProductName).map(([productName, groupedProducts]) => {
          const groupByPartnerKey = groupBy(groupedProducts, 'partnerKey');
          const groupedList = Object.fromEntries(
            Object.entries(groupByPartnerKey).map(([partnerKey, products1]) => {
              const sum = products1.reduce((acc, cur) => acc + Number(cur.quantity), 0);
              return [partnerKey, sum];
            })
          );
          return [productName, groupedList];
        })
      );
      return [supplierName, groupedList];
    })
  );

  return { productForSupplierEntry, issueKeys };
};

/**
 * 계란과 같은 특정 상품을 담당자별 특정 단위로 묶어서 정리한다.
 */
export const getPackingInfoProductListForPartner = async (
  paperInvoices: PaperInvoice[],
  supplierIds: string[],
  purchaseBundles: ('all' | 'store' | 'max' | 'storeGroup')[]
): Promise<PackingInfoProductEntry> => {
  const inventoriesForSupplier = await getProductInventories([['purchaseConfig.supplierId', 'in', supplierIds]]);
  const inventoriesForPurchaseBundles = inventoriesForSupplier.filter((i) =>
    purchaseBundles.includes(i.purchaseConfig.purchaseBundle)
  );

  const filteredProducts = paperInvoices
    .map((invoice) =>
      (invoice.order?.products ?? []).map((p) => ({
        ...p,
        sortKey: invoice.sortKey,
        storeId: invoice.storeId,
      }))
    )
    .flat()
    .filter((p) => inventoriesForPurchaseBundles.some((i) => i._id === p.inventoryId));

  // 품명별로 묶는다. productId 로 묶으면 거래처만 다른 같은 품명 상품이 서로 덮어써 건수가 사라진다.
  // 파트너스 앱 스냅샷 재구성(품명 → 담당 → 묶음 → 건수)과 같은 의미.
  const groupByVolume = Object.fromEntries(
    Object.entries(groupBy(filteredProducts, 'fullName')).map(([productName, products]) => {
      // 담당자 순서별 → 수량별로 묶는다.
      const list = Object.fromEntries(
        Object.entries(groupBy(products, 'sortKey')).map(([sortKey, products0]) => {
          const groupedList = Object.fromEntries(
            Object.entries(groupBy(products0, 'volume')).map(([volume, products1]) => [volume, products1.length])
          );
          return [sortKey, groupedList];
        })
      );
      return [productName, list];
    })
  );

  return groupByVolume;
};

/**
 * 기타 인쇄물 주제(계란 등)에 해당하는 재고 id 집합.
 */
export const getPackingInfoInventoryIds = async (
  supplierIds: string[],
  purchaseBundles: ('all' | 'store' | 'max' | 'storeGroup')[]
) => {
  const inventories = await getProductInventories([['purchaseConfig.supplierId', 'in', supplierIds]]);
  return new Set(
    inventories.filter((i) => purchaseBundles.includes(i.purchaseConfig.purchaseBundle)).map((i) => i._id)
  );
};

/** 이슈배송 말머리. 새 콘솔(supply-console handouts/lib/constants.ts)과 같아야 한다. */
const ISSUE_TASK_PREFIX: Record<IssueTaskCategory, string> = {
  [IssueTaskCategory.DELIVERY]: '★배송-',
  [IssueTaskCategory.PICK_UP]: '☆회수-',
  [IssueTaskCategory.QUICK]: '※퀵-',
  [IssueTaskCategory.REFUND]: '※환불-',
  [IssueTaskCategory.ADDITIONAL_CHARGE]: '※추가청구-',
  [IssueTaskCategory.OTHER]: '※기타-',
  [IssueTaskCategory.INTERNAL]: '※내부-',
  [IssueTaskCategory.REQUEST]: '※요청응대-',
};

/**
 * 파트너스 앱 공유용 스냅샷(원시 행)을 만든다. 문서 형태·재구성 규칙은 `PackingInfoSnapshot` JSDoc 참고.
 * 새 콘솔(supply-console handouts/lib/packing-snapshot.ts)과 같은 값을 만들어야 한다.
 * @param subjects 주제 키·라벨과 그 주제에 해당하는 재고 id 집합 (탭 순서대로)
 */
export const buildPackingInfoSnapshot = async (
  paperInvoices: PaperInvoice[],
  subjects: { key: string; label: string; inventoryIds: Set<string> }[],
  productSuppliers: ProductSupplierDoc[]
): Promise<Pick<PackingInfoSnapshot, 'subjects' | 'products' | 'rows'>> => {
  const products: PackingInfoSnapshot['products'] = [];
  const rowCounts = new Map<string, PackingInfoSnapshot['rows'][number]>();
  const productIndex = new Map<string, number>();
  const labelSupplier = (supplierId: string | null | undefined) =>
    getSupplierLabel(productSuppliers.find((ps) => ps._id === supplierId));

  /** (name, supplier)가 같으면 한 항목 — 주문 상품·이슈 상품 공통 */
  const indexOf = (name: string, supplier: string, isBag: boolean, keys: string[]) => {
    const id = `${supplier}|${name}`;
    const p = productIndex.get(id) ?? products.length;
    if (p === products.length) {
      productIndex.set(id, p);
      products.push({ name, supplier, isBag, subjects: [] });
    }
    keys.forEach((key) => {
      const subject = key as PackingInfoSnapshot['subjects'][number]['key'];
      if (!products[p].subjects.includes(subject)) products[p].subjects.push(subject);
    });
    return p;
  };
  const addRow = (p: number, k: number, v: number, i: boolean) => {
    const id = `${p}|${k}|${v}|${i}`;
    const row = rowCounts.get(id);
    if (row) row.c += 1;
    else rowCounts.set(id, { p, k, v, c: 1, i });
  };

  paperInvoices.forEach((invoice) =>
    (invoice.order?.products ?? []).forEach((product) => {
      const keys = subjects.filter((s) => s.inventoryIds.has(product.inventoryId)).map((s) => s.key);
      const p = indexOf(product.fullName, labelSupplier(product.supplierId), product.packing === 'bag', keys);
      addRow(p, invoice.sortKey, product.volume, false);
    })
  );

  // 이슈배송 — 상품 문서로 품명·거래처를 되살려 주문 상품과 같은 항목을 가리킨다. 못 찾으면 '기타' 별도 항목.
  const issueRows = paperInvoices.flatMap((invoice) =>
    (invoice.issueTasks ?? []).flatMap((task: IssueTaskDoc) => {
      const prefix = ISSUE_TASK_PREFIX[task.category] ?? '';
      const taskProducts = task.products ?? [];
      if (taskProducts.length === 0)
        return [{ k: invoice.sortKey, id: '', label: `${prefix} ${task.privateNote ?? ''}`, v: 1 }];
      return taskProducts.map((product) => ({
        k: invoice.sortKey,
        id: product.productId,
        label: `${prefix} ${product.productName}`,
        v: product.quantity,
      }));
    })
  );
  const productMap = await getProductMapByIds(issueRows.map((row) => row.id));
  issueRows.forEach((row) => {
    const doc = productMap[row.id];
    const p = doc
      ? indexOf(doc.fullName, labelSupplier(doc.supplierId), doc.packing === 'bag', [])
      : indexOf(row.label, '기타', false, []);
    addRow(p, row.k, row.v, true);
  });

  return {
    subjects: subjects.map(({ key, label }) => ({
      key: key as PackingInfoSnapshot['subjects'][number]['key'],
      label,
    })),
    products,
    rows: [...rowCounts.values()],
  };
};
